import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  Icon,
  IconButton,
  Select,
  Textarea,
  Toast,
  Tooltip,
} from '../../design';
import { useAuth } from '../../auth/AuthProvider';
import { CalEmbed } from '../../components/CalEmbed';
import { ApiError } from '../../lib/api';
import { audioApi, audioKindIcon } from '../../lib/audio';
import { clientsApi, clientStatusTone, formatDate, inviteTone, type ClientUpdateInput } from '../../lib/clients';
import {
  CLIENT_GOALS,
  CLIENT_STATUS_LABELS,
  INVITE_LABELS,
  GRANT_SOURCE_LABELS,
  type AudioItem,
  type ClientDetail,
  type ClientStatus,
  type PractitionerOption,
} from '../../lib/types';

const STATUS_OPTIONS = (Object.keys(CLIENT_STATUS_LABELS) as ClientStatus[]).map((value) => ({
  value,
  label: CLIENT_STATUS_LABELS[value],
}));

const labelStyle = {
  font: 'var(--type-label)',
  letterSpacing: 'var(--track-label)',
  textTransform: 'uppercase' as const,
  color: 'var(--text-muted)',
};

interface EditForm {
  name: string;
  goal: string;
  program: string;
  intakeNotes: string;
  practitionerId: string;
}

/** Nullable columns bound to inputs must go through '' or React flips them to uncontrolled. */
function toForm(client: ClientDetail): EditForm {
  return {
    name: client.name,
    goal: client.goal ?? '',
    program: client.program ?? '',
    intakeNotes: client.intakeNotes ?? '',
    practitionerId: client.practitionerId ?? '',
  };
}

export function ClientDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [savingStatus, setSavingStatus] = useState(false);
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState(false);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await clientsApi.get(id);
      setClient(res.client);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setMissing("We couldn't find that client, or they're not assigned to you.");
      } else if (err instanceof ApiError && err.status === 403) {
        setMissing("You don't have access to this client.");
      } else {
        setError('Could not load this client.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function changeStatus(status: ClientStatus) {
    if (!client) return;
    const previous = client.status;
    setSavingStatus(true);
    try {
      const res = await clientsApi.update(client.id, { status });
      setClient(res.client);
    } catch (err) {
      setClient({ ...client, status: previous });
      setError(err instanceof ApiError ? err.message : 'Could not change the status.');
    } finally {
      setSavingStatus(false);
    }
  }

  async function resend() {
    if (!client) return;
    setSending(true);
    setError(null);
    try {
      const res = await clientsApi.resendInvite(client.id);
      setClient(res.client);
      setNotice(`Invite re-sent to ${res.client.email}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resend the invitation.');
    } finally {
      setSending(false);
    }
  }

  function openEdit() {
    if (!client) return;
    setForm(toForm(client));
    setFormError(null);
    setEditing(true);
    if (isAdmin && practitioners.length === 0) {
      void clientsApi
        .practitioners()
        .then((r) => setPractitioners(r.practitioners))
        .catch(() => undefined);
    }
  }

  function closeEdit() {
    // Dialog also closes on a scrim click and gives no way to tell that apart from
    // the X, so a stray click would otherwise discard a paragraph of intake notes.
    if (client && form && JSON.stringify(form) !== JSON.stringify(toForm(client))) {
      if (!window.confirm('Discard your changes?')) return;
    }
    setEditing(false);
  }

  async function save() {
    if (!client || !form) return;
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }

    // Send only what changed, so the server's "Nothing to update" path stays meaningful.
    const original = toForm(client);
    const patch: ClientUpdateInput = {};
    if (form.name.trim() !== original.name) patch.name = form.name.trim();
    if (form.goal !== original.goal) patch.goal = form.goal || null;
    if (form.program.trim() !== original.program) patch.program = form.program.trim() || null;
    if (form.intakeNotes !== original.intakeNotes) patch.intakeNotes = form.intakeNotes.trim() || null;
    if (isAdmin && form.practitionerId !== original.practitionerId) {
      patch.practitionerId = form.practitionerId || null;
    }

    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const res = await clientsApi.update(client.id, patch);
      setClient(res.client);
      setEditing(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not save these changes.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      </Card>
    );
  }

  if (missing || !client) {
    return (
      <Card pad="none">
        <EmptyState
          icon="user-x"
          title="Client not found"
          action={
            <Button variant="ghost" size="sm" icon="chevron-left" onClick={() => navigate('/clients')}>
              All clients
            </Button>
          }
        >
          {missing ?? 'Could not load this client.'}
        </EmptyState>
      </Card>
    );
  }

  return (
    <>
      {notice && (
        <Toast tone="success" title="Invite sent" onDismiss={() => setNotice(null)}>
          {notice}
        </Toast>
      )}
      {error && (
        <Toast tone="danger" title="Something went wrong" onDismiss={() => setError(null)}>
          {error}
        </Toast>
      )}

      {/* Not Breadcrumbs — it renders a raw <a href>, which reloads the whole SPA. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" icon="chevron-left" onClick={() => navigate('/clients')}>
          All clients
        </Button>
        <Avatar name={client.name} size="lg" />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <h2 style={{ font: 'var(--type-h3)', margin: 0 }}>{client.name}</h2>
            <Badge dot tone={clientStatusTone(client.status)}>
              {CLIENT_STATUS_LABELS[client.status]}
            </Badge>
            {client.inviteState !== 'accepted' && (
              <Badge tone={inviteTone(client.inviteState)}>{INVITE_LABELS[client.inviteState]}</Badge>
            )}
          </div>
          <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>{client.email}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-4)' }}>
          {/* Hidden when there is no assigned practitioner with a Cal.com link,
              like Resend invite only renders when applicable. */}
          {client.practitionerCalLink && (
            <Button size="sm" variant="secondary" icon="calendar" onClick={() => setBooking(true)}>
              Book session
            </Button>
          )}
          <Button size="sm" icon="pencil" onClick={openEdit}>
            Edit
          </Button>
        </div>
      </div>

      {booking && client.practitionerCalLink && (
        <Dialog
          open={booking}
          title={`Book a session for ${client.name}`}
          onClose={() => setBooking(false)}
          width={960}
        >
          {/* The embed prefills but doesn't lock identity — it's the
              practitioner's own tool, not enforcement. A scrim-click close
              costs only re-picking a slot; Cal.com holds nothing server-side. */}
          <CalEmbed
            namespace="client-detail"
            calLink={client.practitionerCalLink}
            prefill={{ name: client.name, email: client.email }}
            onBookingSuccessful={() => {
              setBooking(false);
              setNotice(`Session booked for ${client.name} — Cal.com emails the confirmation.`);
            }}
          />
        </Dialog>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 'var(--space-6)', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          <Card title="Profile">
            <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', rowGap: 'var(--space-5)', alignItems: 'center' }}>
              <span style={labelStyle}>Goal</span>
              <span>{client.goal ?? <span style={{ color: 'var(--text-muted)' }}>Not set</span>}</span>

              <span style={labelStyle}>Current program</span>
              <span>{client.program ?? <span style={{ color: 'var(--text-muted)' }}>Not set</span>}</span>

              <span style={labelStyle}>Status</span>
              <Select
                value={client.status}
                options={STATUS_OPTIONS}
                disabled={savingStatus}
                onChange={(e) => void changeStatus(e.target.value as ClientStatus)}
                style={{ height: 'var(--control-h-sm)', width: 200 }}
              />

              <span style={labelStyle}>Practitioner</span>
              <span>{client.practitionerName ?? <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>}</span>

              <span style={labelStyle}>Added</span>
              <span>{formatDate(client.createdAt)}</span>
            </div>
          </Card>

          <Card title="Intake notes">
            {client.intakeNotes ? (
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{client.intakeNotes}</p>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                <span style={{ color: 'var(--text-muted)' }}>No intake notes yet.</span>
                <Button variant="ghost" size="sm" onClick={openEdit}>
                  Add notes
                </Button>
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        <Card title="Account">
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 'var(--space-5)', alignItems: 'center' }}>
            <span style={labelStyle}>Email</span>
            <span style={{ overflowWrap: 'anywhere' }}>{client.email}</span>

            <span style={labelStyle}>Access</span>
            <Badge tone={inviteTone(client.inviteState)}>{INVITE_LABELS[client.inviteState]}</Badge>

            {client.inviteState !== 'accepted' && (
              <>
                <span style={labelStyle}>Invite expires</span>
                <span>{formatDate(client.inviteExpiresAt)}</span>
              </>
            )}

            <span style={labelStyle}>Updated</span>
            <span>{formatDate(client.updatedAt)}</span>
          </div>

          {client.inviteState !== 'accepted' && (
            <div style={{ marginTop: 'var(--space-6)' }}>
              <Button variant="secondary" size="sm" icon="send" block disabled={sending} onClick={() => void resend()}>
                {sending ? 'Sending…' : 'Resend invite'}
              </Button>
            </div>
          )}
        </Card>

        <ProgramsCard clientId={client.id} onNotice={setNotice} onError={setError} />
        </div>
      </div>

      {form && (
        <Dialog
          open={editing}
          title="Edit client"
          onClose={closeEdit}
          width={520}
          footer={
            <>
              <Button variant="ghost" size="sm" onClick={closeEdit}>
                Cancel
              </Button>
              <Button size="sm" disabled={saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <Field label="Full name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Primary goal">
              <Select
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                options={[{ value: '', label: 'Not set' }, ...CLIENT_GOALS.map((g) => ({ value: g, label: g }))]}
              />
            </Field>
            <Field label="Current program">
              <Input value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} />
            </Field>
            {isAdmin && (
              <Field label="Assigned practitioner">
                <Select
                  value={form.practitionerId}
                  onChange={(e) => setForm({ ...form, practitionerId: e.target.value })}
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...practitioners.map((p) => ({ value: p.id, label: p.name })),
                  ]}
                />
              </Field>
            )}
            <Field label="Intake notes">
              <Textarea
                rows={6}
                value={form.intakeNotes}
                onChange={(e) => setForm({ ...form, intakeNotes: e.target.value })}
              />
            </Field>
            {formError && <span style={{ color: 'var(--status-danger)', font: 'var(--type-body-sm)' }}>{formError}</span>}
          </div>
        </Dialog>
      )}
    </>
  );
}

interface ProgramsCardProps {
  clientId: string;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Entitlements for this client: granted programs with source badges, plus an
 * inline grant affordance. One fetch (GET /api/audio?clientId=) feeds both the
 * list and the grant options. Store purchases are permanent by policy — no
 * revoke control on woo rows.
 */
function ProgramsCard({ clientId, onNotice, onError }: ProgramsCardProps) {
  const [rows, setRows] = useState<AudioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [grantId, setGrantId] = useState('');
  const [busy, setBusy] = useState(false);

  const loadPrograms = useCallback(async () => {
    try {
      const res = await audioApi.list(clientId);
      setRows(res.files);
    } catch {
      onError('Could not load programs.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    void loadPrograms();
  }, [loadPrograms]);

  const granted = rows.filter((r) => r.entitled);
  const grantable = rows.filter((r) => !r.entitled && r.published);

  async function grant() {
    if (!grantId) return;
    setBusy(true);
    try {
      await audioApi.grant(grantId, clientId);
      const title = rows.find((r) => r.id === grantId)?.title ?? 'Program';
      setGrantId('');
      await loadPrograms();
      onNotice(`"${title}" granted.`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not grant the program.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(row: AudioItem) {
    if (!window.confirm(`Remove "${row.title}" from this client?`)) return;
    try {
      await audioApi.revoke(row.id, clientId);
      await loadPrograms();
      onNotice(`"${row.title}" removed.`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not remove the program.');
    }
  }

  return (
    <Card title="Programs">
      {loading ? (
        <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>Loading…</span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {granted.length === 0 && (
            <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>No programs granted yet.</span>
          )}
          {granted.map((row) => (
            <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <Icon name={audioKindIcon(row.kind)} size="16px" style={{ color: 'var(--navy-300)' }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.title}
              </span>
              {row.source === 'woo' ? (
                <Tooltip label="Granted by a store purchase">
                  <Badge tone="brand">{GRANT_SOURCE_LABELS.woo}</Badge>
                </Tooltip>
              ) : (
                <>
                  <Badge tone="neutral">{row.source ? GRANT_SOURCE_LABELS[row.source] : 'Manual'}</Badge>
                  <IconButton icon="x" label={`Remove ${row.title}`} size="sm" onClick={() => void revoke(row)} />
                </>
              )}
            </div>
          ))}
          {grantable.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
              <Select
                value={grantId}
                onChange={(e) => setGrantId(e.target.value)}
                options={[{ value: '', label: 'Grant a program…' }, ...grantable.map((r) => ({ value: r.id, label: r.title }))]}
                style={{ flex: 1, height: 'var(--control-h-sm)' }}
              />
              <Button size="sm" variant="secondary" disabled={!grantId || busy} onClick={() => void grant()}>
                Grant
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
